<template>
  <div class="login-pharma-bg">

    <!-- Decorative pill blobs -->
    <img :src="require('@/assets/images/PillMED.svg')" class="deco-pill deco-pill--tl" aria-hidden="true" />
    <img :src="require('@/assets/images/PillPARA.svg')" class="deco-pill deco-pill--br" aria-hidden="true" />

    <div class="login-card-wrapper">
      <b-card class="login-card shadow-lg" no-body>
        <b-card-body class="p-4 p-md-5">

          <!-- Logo -->
          <div class="text-center mb-3">
            <img :src="require('@/assets/images/logo/prophasud-logo.png')" alt="Prophasud" class="login-logo" />
          </div>

          <b-card-title title-tag="h2" class="font-weight-bold mb-25 text-center">
            Bienvenue 👋
          </b-card-title>
          <b-card-text class="text-muted text-center mb-3" style="font-size:0.9rem;">
            Veuillez saisir vos identifiants pour accéder à la plateforme
          </b-card-text>

          <b-overlay :show="showLoading" rounded="sm">
            <b-form class="mt-2" @submit.prevent="">

              <b-form-group label="Nom d'utilisateur" label-for="login-email">
                <b-form-input
                  id="login-email"
                  v-model="userEmail"
                  name="login-email"
                  placeholder="prophasud-clt-000001"
                  size="lg"
                  autofocus
                />
              </b-form-group>

              <b-form-group class="mt-1">
                <label for="login-password">Mot de passe</label>
                <b-input-group class="input-group-merge">
                  <b-form-input
                    id="login-password"
                    v-model="password"
                    class="form-control-merge"
                    :type="passwordFieldType"
                    name="login-password"
                    placeholder="············"
                    size="lg"
                    @keyup.enter="login()"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>

              <b-button
                type="submit"
                variant="primary"
                block
                size="lg"
                class="mt-2 login-btn"
                @click="login()"
              >
                <feather-icon icon="UnlockIcon" class="mr-50" />
                <span class="align-middle">Connexion</span>
              </b-button>

            </b-form>
          </b-overlay>

        </b-card-body>
      </b-card>

      <p class="text-center mt-2" style="color:rgba(255,255,255,0.6); font-size:0.78rem;">
        © {{ new Date().getFullYear() }} Prophasud — Plateforme PharmaConnect
      </p>
    </div>
  </div>
</template>

<script>
import { required, email } from '@validations'
import { togglePasswordVisibility } from '@core/mixins/ui/forms'
import useJwt from "@/auth/jwt/useJwt";
import { getHomeRouteForLoggedInUser, isUserLoggedIn } from "@/auth/utils";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { initialAbility } from "@/libs/acl/config";

export default {
  metaInfo() {
    return {
      title: "PharmaConnect - Connexion",
      meta: [
        { vmid: "description", name: "description", content: "Connectez-vous à PharmaConnect, la plateforme numérique pour faciliter la communication et la gestion des commandes pharmaceutiques en Tunisie." },
        { name: "robots", content: "noindex, nofollow" },
        { property: "og:title", content: "PharmaConnect - Connexion" },
        { property: "og:description", content: "Connectez-vous à PharmaConnect pour accéder à la plateforme numérique qui facilite la communication et la gestion des commandes pharmaceutiques." },
        { property: "og:image", content: "https://www.pharmaconnect.net/images/login-thumbnail.jpg" },
        { property: "og:url", content: "https://www.pharmaconnect.net/login" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "PharmaConnect - Connexion" },
        { name: "twitter:description", content: "Connectez-vous à PharmaConnect pour une gestion efficace de vos commandes pharmaceutiques." },
        { name: "twitter:image", content: "https://www.pharmaconnect.net/images/login-thumbnail.jpg" },
      ],
      link: [{ rel: "canonical", href: "https://www.pharmaconnect.net/login" }],
      script: [{
        type: "application/ld+json",
        json: {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "PharmaConnect - Connexion",
          "description": "Connectez-vous à PharmaConnect, la plateforme numérique qui facilite la communication et la gestion des commandes pharmaceutiques en Tunisie.",
          "publisher": { "@type": "Organization", "name": "PharmaConnect", "url": "https://www.pharmaconnect.net" },
        },
      }],
    };
  },

  mixins: [togglePasswordVisibility],
  data() {
    return {
      showLoading: false,
      password: '',
      userEmail: '',
      required,
      email,
    }
  },
  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === 'password' ? 'EyeIcon' : 'EyeOffIcon'
    },
  },
  methods: {
    // Fixes what browser password managers auto-fill from the OLD portal on the
    // same domain (old prefix PROPHASUSD-, underscores, stray spaces, casing).
    // The corrected value is written back into the visible field before the
    // submit, so on a successful login the browser offers to save the REAL
    // identifier and the customer's vault self-heals. Only customer-style
    // logins (prophasu...) are touched: administrator, sub-accounts and
    // supplier usernames pass through unchanged.
    normalizeUsername(raw) {
      if (!raw) return '';
      let username = raw.trim().replace(/\s+/g, '');
      if (/^prophasu/i.test(username)) {
        // Canonical account format is LOWERCASE (NavCustomerSyncService creates
        // username and initial password as prophasud-clt-<code>); the username
        // lookup is case-insensitive but the bcrypt password is exact-match, so
        // lowercase is the only safe normalization target.
        username = username.toLowerCase().replace(/_/g, '-').replace(/^prophasusd-/, 'prophasud-');
      }
      return username;
    },
    login() {
      const rawUsername = this.userEmail;
      const rawPassword = this.password;
      this.userEmail = this.normalizeUsername(rawUsername);
      // Initial-credentials pattern (welcome email: identifiant et mot de passe
      // identiques): the password is "the customer code" whenever, interpreted
      // as a username, it normalizes to the SAME canonical value — this covers
      // any case variant, the old prefix and underscores (e.g. a browser vault
      // holding the lowercase username with the old UPPERCASE password, since
      // password managers record what was filled and ignore our JS write-back).
      // A real personal password never normalizes into the username, so any
      // other password is sent untouched.
      if (rawPassword && /^prophasu/i.test(this.userEmail)
        && this.normalizeUsername(rawPassword) === this.userEmail) {
        this.password = this.userEmail;
      }
      this.showLoading = true;
      useJwt.login({ email: this.userEmail, password: this.password })
        .then(response => {
          this.showLoading = false;
          if (response.data.code == '200') {
            if (!localStorage.getItem("lastItemUpdate")) {
              localStorage.setItem("lastItemUpdate", "01-01-2000 - 00:00:00");
            }
            if (!localStorage.getItem('lastBlockedSync')) {
              localStorage.setItem('lastBlockedSync', new Date().toISOString().slice(0, 10));
            }
            const userData = response.data;
            useJwt.setToken(userData);
            useJwt.setUserData(userData);
            this.$ability.update(userData.ability);
            if (['ROLE_CUSTOMER', 'ROLE_EDITOR', 'ROLE_SUPPLIER'].includes(userData.role) && userData.firstTime) {
              this.$router.push({ name: 'password' });
            } else {
              this.$router.replace(getHomeRouteForLoggedInUser(userData.role));
            }
          } else {
            this.$toast.clear();
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Authentification invalide',
                icon: 'XIcon',
                text: "Nom d'utilisateur ou mot de passe incorrect. Si votre navigateur a rempli ces champs automatiquement, il s'agit probablement de vos anciens identifiants : utilisez le mot de passe initial communiqué par email, votre navigateur proposera ensuite d'enregistrer le nouveau.",
                variant: 'danger',
              },
            }, { timeout: 15000 });
          }
        }).catch((error) => {
          this.showLoading = false;
          console.error(error);
        });
    },
    logout() {
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName);
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName);
      localStorage.removeItem('userData');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('company');
      this.$ability.update(initialAbility);
      this.$forceUpdate();
    },
  },
  mounted() {
    if (!isUserLoggedIn()) {
      this.logout();
    } else {
      this.$router.replace(getHomeRouteForLoggedInUser(JSON.parse(localStorage.getItem('userData')).role));
    }
  },
}
</script>

<style lang="scss" scoped>
/* ── Background ─────────────────────────────────────────── */
.login-pharma-bg {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  /* Pharmacy photo + brand colour overlay */
  background:
    linear-gradient(135deg, rgba(11,61,107,0.78) 0%, rgba(94,80,238,0.72) 100%),
    url('~@/assets/images/login-bg.jpg') center / cover no-repeat;
}

/* ── Decorative pill SVGs ───────────────────────────────── */
.deco-pill {
  position: absolute;
  opacity: 0.08;
  pointer-events: none;
  user-select: none;
}
.deco-pill--tl {
  width: 340px;
  top: -80px;
  left: -80px;
  transform: rotate(-30deg);
}
.deco-pill--br {
  width: 280px;
  bottom: -60px;
  right: -60px;
  transform: rotate(20deg);
}

/* ── Card ───────────────────────────────────────────────── */
.login-card-wrapper {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 440px;
  padding: 1rem;
}

.login-card {
  border: none;
  border-radius: 1.25rem !important;
  backdrop-filter: blur(6px);
  background: rgba(255, 255, 255, 0.97) !important;
}

.login-logo {
  height: 52px;
}

/* ── Button ─────────────────────────────────────────────── */
.login-btn {
  letter-spacing: 0.03em;
  font-weight: 600;
  border-radius: 0.6rem !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}
</style>
